import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkOperationsModal = ({ selectedClients, clients, onClose, onComplete }) => {
  const [operation, setOperation] = useState('policy_update');
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  const selectedClientData = clients?.filter(c => selectedClients?.includes(c?.id));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setProcessing(true);

    try {
      // Simulate bulk operation
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log(`Bulk operation ${operation} completed for ${selectedClients?.length} clients`);
      onComplete?.();
    } catch (error) {
      console.error('Bulk operation failed:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground">Bulk Operations</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Apply changes to {selectedClients?.length} selected client{selectedClients?.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Operation Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Operation Type
            </label>
            <select
              value={operation}
              onChange={(e) => setOperation(e?.target?.value)}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              required
            >
              <option value="policy_update">Mass Policy Update</option>
              <option value="compliance_reminder">Compliance Reminder</option>
              <option value="announcement">System Announcement</option>
              <option value="billing_adjustment">Billing Adjustment</option>
              <option value="feature_toggle">Feature Toggle</option>
            </select>
          </div>

          {/* Selected Clients Preview */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Selected Organizations ({selectedClientData?.length})
            </label>
            <div className="bg-muted rounded-md p-3 max-h-32 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {selectedClientData?.map(client => (
                  <span
                    key={client?.id}
                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-primary/10 text-primary"
                  >
                    {client?.companyName}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Message/Details */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {operation === 'announcement' ? 'Announcement Message' : 'Operation Details'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder={
                operation === 'policy_update' ?'Describe the policy changes being applied...'
                  : operation === 'compliance_reminder' ?'Enter reminder message for compliance requirements...'
                  : operation === 'announcement' ?'Enter system announcement message...' :'Enter operation details...'
              }
              rows={6}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>

          {/* Operation Info */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground mb-1">Operation Impact</h4>
                <p className="text-sm text-muted-foreground">
                  {operation === 'policy_update' && 'This will update policy configurations for all selected organizations. Changes will be applied immediately.'}
                  {operation === 'compliance_reminder'&& 'Email notifications will be sent to all administrators of selected organizations.'}
                  {operation === 'announcement'&& 'System announcement will be displayed in all selected organizations\' dashboards.'}
                  {operation === 'billing_adjustment'&& 'Billing changes will be reflected in the next billing cycle for selected organizations.'}
                  {operation === 'feature_toggle'&& 'Feature access will be modified for all selected organizations immediately.'}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={processing}
              disabled={!message?.trim()}
            >
              <Icon name="Send" size={16} className="mr-2" />
              {processing ? 'Processing...' : 'Execute Operation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkOperationsModal;